import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AuthService } from 'crqlar-react-common/dist/services/auth/auth.service'
import {
    Box,
    Button,
    Snackbar,
    Alert,
    Stack,
    Typography,
    TextField,
    FormGroup,
    FormLabel,
} from '@mui/material'

interface Props {}

const ResetPasswordForm = ({}: Props) => {
    const { t } = useTranslation()
    const [email, setEmail] = useState<string>('')
    const [emailSent, setEmailSent] = useState<boolean>(false)
    const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    })

    const handlePasswordReset = async () => {
        try {
            const response = await AuthService.resetPassword(email.trim().toLowerCase())
            if (response.error) {
                setSnack({ open: true, message: response.error, severity: 'error' })
            }
            else {
                setSnack({ open: true, message: response.message ?? t('A password-reset email has been sent. Please check your inbox.'), severity: 'success' })
                setEmailSent(true)
            }
        } catch {
            setSnack({ open: true, message: t('Failed to send reset email. Please try again.'), severity: 'error' })
        }
    }

    const closeSnack = () => {
        setSnack(prev => ({ ...prev, open: false }))
    }

    return (
        <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto', p: 3 }}>
            {emailSent ? (
                <Stack spacing={2} alignItems="center" sx={{ mt: 4 }}>
                    <Typography variant="h6" textAlign="center">
                        {t('Check your email')}
                    </Typography>
                    <Typography textAlign="center">
                        {t('If an account exists for the address provided, a reset link has been sent.')}
                    </Typography>
                </Stack>
            ) : (
                <Stack component="form" spacing={2} onSubmit={e => e.preventDefault()}>
                    <Typography variant="h6">{t('Reset password')}</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {t('Enter your email address below and we’ll send you a link to reset your password.')}
                    </Typography>
                    <FormGroup>
                        <FormLabel>{t('Email')}</FormLabel>
                        <TextField
                            required
                            fullWidth
                            size="small"
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </FormGroup>
                    <Button fullWidth variant="contained" onClick={handlePasswordReset} disabled={!email.trim()}>
                        {t('Reset Password')}
                    </Button>
                </Stack>
            )}
            <Snackbar open={snack.open} autoHideDuration={12000} onClose={closeSnack} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: '100%' }}>
                    {snack.message}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default ResetPasswordForm
